import React, { Component, useEffect } from 'react';
import { array, bool, func, oneOf, object, shape, string, arrayOf, oneOfType } from 'prop-types';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { useHistory, useLocation } from 'react-router-dom';
import { Form as FinalForm } from 'react-final-form';
import omit from 'lodash/omit';
import classNames from 'classnames';

import { useConfiguration } from '../../context/configurationContext';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';

import { useIntl, intlShape, FormattedMessage } from '../../util/reactIntl';
import {
  isAnyFilterActive,
  isMainSearchTypeKeywords,
  isOriginInUse,
  getQueryParamNames,
} from '../../util/search';
import {
  NO_ACCESS_PAGE_USER_PENDING_APPROVAL,
  NO_ACCESS_PAGE_VIEW_LISTINGS,
  parse,
} from '../../util/urlHelpers';
import { createResourceLocatorString } from '../../util/routes';
import { propTypes, SCHEMA_TYPE_LONG } from '../../util/types';
import {
  isErrorNoViewingPermission,
  isErrorUserPendingApproval,
  isForbiddenError,
} from '../../util/errors';
import { hasPermissionToViewData, isUserAuthorized } from '../../util/userHelpers';
import { getListingsById } from '../../ducks/marketplaceData.duck';
import { favoriteListingIds, isCustomer } from '../../util/favorites';
import { manageDisableScrolling, isScrollingDisabled } from '../../ducks/ui.duck';

import { Form, H3, H5, NamedLink, NamedRedirect, Page } from '../../components';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';
import FilterLocation from '../PageBuilder/Primitives/SearchCTA/FilterLocation/FilterLocation';

import {
  groupListingFieldConfigs,
  initialValues,
  searchParamsPicker,
  validUrlQueryParamsFromProps,
  validFilterParams,
  cleanSearchFromConflictingParams,
  createSearchResultSchema,
  pickListingFieldFilters,
  omitLimitedListingFieldParams,
  getDatesAndSeatsMaybe,
} from './SearchPage.shared';

import FilterComponent from './FilterComponent';
import FavoritesFilterCheckbox from './FavoritesFilterCheckbox';
import MainPanelHeader from './MainPanelHeader/MainPanelHeader';
import SearchFiltersMobile from './SearchFiltersMobile/SearchFiltersMobile';
import SortBy from './SortBy/SortBy';
import SearchResultsPanel from './SearchResultsPanel/SearchResultsPanel';
import NoSearchResultsMaybe from './NoSearchResultsMaybe/NoSearchResultsMaybe';

import css from './SearchPage.module.css';
import { loadData as loadSearchData } from './SearchPage.duck';

const MODAL_BREAKPOINT = 768; // Search is in modal on mobile layout

// SortBy component has its content in dropdown-popup.
// With this offset we move the dropdown a few pixels on desktop layout.
const FILTER_DROPDOWN_OFFSET = -14;

export class SearchPageComponent extends Component {
  constructor(props) {
    super(props);

    this.state = {
      isMobileModalOpen: false,
      currentQueryParams: validUrlQueryParamsFromProps(props),
    };

    this.onOpenMobileModal = this.onOpenMobileModal.bind(this);
    this.onCloseMobileModal = this.onCloseMobileModal.bind(this);

    // Filter functions
    this.resetAll = this.resetAll.bind(this);
    this.getHandleChangedValueFn = this.getHandleChangedValueFn.bind(this);

    // SortBy
    this.handleSortBy = this.handleSortBy.bind(this);
  }

  componentDidUpdate(prevProps) {
    const {
      currentUser,
      location,
      config,
      onReloadFavorites,
      favoritesPage,
      pagination,
      searchInProgress,
      searchParams,
      history,
      routeConfiguration,
    } = this.props;
    if (
      (favoritesPage || parse(location.search).favorites === 1) &&
      JSON.stringify(favoriteListingIds(prevProps.currentUser)) !==
        JSON.stringify(favoriteListingIds(currentUser))
    ) {
      onReloadFavorites(location.search, config);
    }

    const queryParams = parse(location.search);
    const requestedPage = Number(queryParams.page) || 1;
    const lastPage = Math.max(1, pagination?.totalPages || 1);
    if (
      favoritesPage &&
      prevProps.pagination !== pagination &&
      !searchInProgress &&
      searchParams?.favorites === 1 &&
      Number(searchParams.page) === requestedPage &&
      requestedPage > lastPage
    ) {
      const { page, ...filters } = queryParams;
      history.replace(
        createResourceLocatorString(
          'FavoritesPage',
          routeConfiguration,
          {},
          lastPage > 1 ? { ...filters, page: lastPage } : filters
        )
      );
    }
  }

  // Invoked when a modal is opened from a child component,
  // for example when a filter modal is opened in mobile view
  onOpenMobileModal() {
    this.setState({ isMobileModalOpen: true });
  }

  // Invoked when a modal is closed from a child component,
  // for example when a filter modal is opened in mobile view
  onCloseMobileModal() {
    this.setState({ isMobileModalOpen: false });
  }

  // Reset all filter query parameters
  resetAll(e) {
    const { history, routeConfiguration, config, routeName = 'SearchPage' } = this.props;
    const { listingFields: listingFieldsConfig } = config?.listing || {};
    const { defaultFilters: defaultFiltersConfig } = config?.search || {};

    const urlQueryParams = validUrlQueryParamsFromProps(this.props);
    const filterQueryParamNames = getQueryParamNames(listingFieldsConfig, defaultFiltersConfig);

    // Reset state
    this.setState({ currentQueryParams: {} });

    // Reset routing params, including the location filter (address/bounds/origin
    // aren't part of filterQueryParamNames since they're handled outside MainPanel).
    const queryParams = omit(urlQueryParams, [
      ...filterQueryParamNames,
      'address',
      'bounds',
      'origin',
      'favorites',
    ]);
    history.push(createResourceLocatorString(routeName, routeConfiguration, {}, queryParams));
  }

  getHandleChangedValueFn(useHistoryPush) {
    const { history, routeConfiguration, config, routeName = 'SearchPage' } = this.props;
    const { listingFields: listingFieldsConfig } = config?.listing || {};
    const { defaultFilters: defaultFiltersConfig, sortConfig } = config?.search || {};
    const listingCategories = config.categoryConfiguration.categories;
    const filterConfigs = {
      listingFieldsConfig,
      defaultFiltersConfig,
      listingCategories,
    };

    const urlQueryParams = validUrlQueryParamsFromProps(this.props);

    return updatedURLParams => {
      const updater = prevState => {
        const { address, bounds, keywords } = urlQueryParams;
        const mergedQueryParams = { ...urlQueryParams, ...prevState.currentQueryParams };

        // Address and bounds are handled outside of MainPanel.
        // I.e. TopbarSearchForm && search by moving the map.
        // We should always trust urlQueryParams with those.
        // The same applies to keywords, if the main search type is keyword search.
        const keywordsMaybe = isMainSearchTypeKeywords(config) ? { keywords } : {};
        const datesAndSeatsMaybe = getDatesAndSeatsMaybe(mergedQueryParams, updatedURLParams);
        return {
          currentQueryParams: omitLimitedListingFieldParams(
            {
              ...mergedQueryParams,
              ...updatedURLParams,
              ...keywordsMaybe,
              ...datesAndSeatsMaybe,
              address,
              bounds,
            },
            filterConfigs
          ),
        };
      };

      const callback = () => {
        if (useHistoryPush) {
          const searchParams = this.state.currentQueryParams;
          const search = cleanSearchFromConflictingParams(searchParams, filterConfigs, sortConfig);
          history.push(createResourceLocatorString(routeName, routeConfiguration, {}, search));
        }
      };

      this.setState(updater, callback);
    };
  }

  handleSortBy(urlParam, values) {
    const { history, routeConfiguration, routeName = 'SearchPage' } = this.props;
    const urlQueryParams = validUrlQueryParamsFromProps(this.props);

    const queryParams = values
      ? { ...urlQueryParams, [urlParam]: values }
      : omit(urlQueryParams, urlParam);

    history.push(createResourceLocatorString(routeName, routeConfiguration, {}, queryParams));
  }

  // Reset all filter query parameters
  handleResetAll(e) {
    this.resetAll(e);

    // blur event target if event is passed
    if (e && e.currentTarget) {
      e.currentTarget.blur();
    }
  }

  // onLocalChange, when given, routes the selected place into the bottom
  // sheet's pending filter buffer (like every other filter there) instead of
  // navigating immediately — so "Redefinir tudo"/"Ver resultados" apply to it too.
  renderLocationFilter(fieldWrapperClassName, compact, onLocalChange) {
    const { history, routeConfiguration, config } = this.props;
    const routeName = this.props.routeName || 'SearchPage';
    const { address } = validUrlQueryParamsFromProps(this.props);

    return (
      <FinalForm
        // Remounts the field (clearing its displayed text) whenever the address
        // leaves the URL, e.g. via "Redefinir tudo" — this form's value is local
        // to the field and isn't otherwise tied to the URL query params.
        key={address || 'no-location'}
        onSubmit={() => {}}
        render={({ handleSubmit, values }) => {
          useEffect(() => {
            if (values?.location?.selectedPlace) {
              const {
                search,
                selectedPlace: { origin, bounds },
              } = values.location;
              const queryParams = { bounds, address: search };
              if (isOriginInUse(config) && origin) {
                queryParams.origin = `${origin.lat},${origin.lng}`;
              }
              if (onLocalChange) {
                onLocalChange(queryParams);
              } else {
                const to = createResourceLocatorString(
                  routeName,
                  routeConfiguration,
                  {},
                  queryParams
                );
                history.push(to);
              }
            }
          }, [values]);
          return (
            <Form role="search" onSubmit={handleSubmit}>
              <div className={fieldWrapperClassName} key="locationSearch">
                <FilterLocation
                  setSubmitDisabled={() => {}}
                  alignLeft={() => {}}
                  className={css.locationFilterComponent}
                  compact={compact}
                />
              </div>
            </Form>
          );
        }}
      />
    );
  }

  render() {
    const {
      intl,
      listings,
      history,
      location,
      onManageDisableScrolling,
      pagination,
      scrollingDisabled,
      searchInProgress,
      searchListingsError,
      searchParams,
      routeConfiguration,
      config,
      hideFilterSidebar = false,
      favoritesPage = false,
      currentUser,
    } = this.props;

    const { listingFields } = config?.listing || {};
    const { defaultFilters: defaultFiltersConfig, sortConfig } = config?.search || {};
    // console.log("dekho ", sortConfig)
    const activeListingTypes = config?.listing?.listingTypes.map(config => config.listingType);
    const marketplaceCurrency = config.currency;
    const categoryConfiguration = config.categoryConfiguration;
    const listingCategories = categoryConfiguration.categories;
    const listingFieldsConfig = pickListingFieldFilters({
      listingFields,
      locationSearch: location.search,
      categoryConfiguration,
    });
    const filterConfigs = {
      listingFieldsConfig,
      defaultFiltersConfig,
      listingCategories,
    };

    // Page transition might initially use values from previous search
    // urlQueryParams doesn't contain page specific url params
    // like mapSearch, page or origin (origin depends on config.maps.search.sortSearchByDistance)
    const { searchParamsAreInSync, urlQueryParams, searchParamsInURL } = searchParamsPicker(
      location.search,
      favoritesPage ? omit(searchParams, 'favorites') : searchParams,
      filterConfigs,
      sortConfig,
      isOriginInUse(config)
    );
    const validQueryParams = urlQueryParams;
    const favoritesSelected = parse(location.search).favorites === 1;
    const setFavoritesFilter = selected => {
      const { page, favorites, ...rest } = parse(location.search);
      const next = selected ? { ...rest, favorites: 1 } : rest;
      history.push(
        createResourceLocatorString(
          this.props.routeName || 'SearchPage',
          routeConfiguration,
          {},
          next
        )
      );
    };
    const favoritesButton =
      !favoritesPage && isCustomer(currentUser) ? (
        <button
          type="button"
          className={classNames(css.favoritesFilter, {
            [css.favoritesFilterFlash]: hideFilterSidebar,
            [css.favoritesFilterActive]: favoritesSelected,
          })}
          aria-label={intl.formatMessage({
            id: 'SearchPage.favorites',
            defaultMessage: 'Favoritos',
          })}
          aria-pressed={favoritesSelected}
          onClick={() => setFavoritesFilter(!favoritesSelected)}
        >
          <span aria-hidden="true">♥</span>
          <span className={hideFilterSidebar ? css.favoritesFilterText : undefined}>
            <FormattedMessage id="SearchPage.favorites" defaultMessage="Favoritos" />
          </span>
        </button>
      ) : null;
    const mobileFavoritesCheckbox =
      !hideFilterSidebar && !favoritesPage && isCustomer(currentUser) ? (
        <FavoritesFilterCheckbox checked={favoritesSelected} onChange={setFavoritesFilter} />
      ) : null;

    const isKeywordSearch = isMainSearchTypeKeywords(config);
    const builtInPrimaryFilters = defaultFiltersConfig.filter(f =>
      ['categoryLevel'].includes(f.key)
    );
    const builtInFilters = isKeywordSearch
      ? defaultFiltersConfig.filter(f => !['keywords', 'categoryLevel'].includes(f.key))
      : defaultFiltersConfig.filter(f => !['categoryLevel'].includes(f.key));
    const [customPrimaryFilters, customSecondaryFilters] = groupListingFieldConfigs(
      listingFieldsConfig,
      activeListingTypes
    );
    const useMapSearchFilters = favoritesPage && config.layout.searchPage?.variantType === 'map';
    const availableFilters = useMapSearchFilters
      ? [...customPrimaryFilters.reverse(), ...customSecondaryFilters]
      : [
          ...builtInPrimaryFilters,
          ...customPrimaryFilters,
          ...builtInFilters,
          ...customSecondaryFilters,
        ];

    // Structured filter items for the bottom-sheet two-panel layout (Flash Search)
    const getFilterLabel = filterConfig => {
      switch (filterConfig.key) {
        case 'keywords':
          return intl.formatMessage({ id: 'FilterComponent.keywordsLabel' });
        case 'dates':
          return intl.formatMessage({ id: 'FilterComponent.datesLabel' });
        case 'price':
          return intl.formatMessage({ id: 'FilterComponent.priceLabel' });
        case 'seats':
          return intl.formatMessage({ id: 'FilterComponent.seatsLabel' });
        default:
          return filterConfig.filterConfig?.label || filterConfig.label || filterConfig.key;
      }
    };
    const filterItems = hideFilterSidebar
      ? availableFilters
          .filter(fc => fc.key !== 'categoryLevel')
          .map(filterConfig => ({
            id: filterConfig.key,
            label: getFilterLabel(filterConfig),
            config: filterConfig,
            // renderContent receives localQueryParams + localHandler from SearchFiltersMobile
            // so filters are rendered against the local (pending) state, not the URL.
            // initialValues must be a function (queryParamNames, isLiveEdit) => {} per FilterComponent contract.
            renderContent: ({
              localQueryParams,
              getHandleChangedValueFn: localHandler,
              onValidityChange,
              registerFlush,
            }) => (
              <FilterComponent
                key={`BottomSheet.${filterConfig.scope || 'built-in'}.${filterConfig.key}`}
                idPrefix="SearchFiltersBottomSheet"
                config={filterConfig}
                listingCategories={listingCategories}
                marketplaceCurrency={marketplaceCurrency}
                urlQueryParams={localQueryParams}
                initialValues={(queryParamNames, _isLiveEdit) => {
                  const names = Array.isArray(queryParamNames) ? queryParamNames : [];
                  return names.reduce((acc, name) => {
                    const v = localQueryParams[name];
                    return v != null ? { ...acc, [name]: v } : acc;
                  }, {});
                }}
                getHandleChangedValueFn={localHandler}
                intl={intl}
                liveEdit
                showAsPopup={false}
                {...(filterConfig.schemaType === SCHEMA_TYPE_LONG
                  ? { onValidityChange, registerFlush }
                  : {})}
              />
            ),
          }))
          .concat([
            {
              id: 'location',
              label: intl.formatMessage({ id: 'PageBuilder.SearchCTA.locationPlaceholder' }),
              config: null,
              renderContent: ({ getHandleChangedValueFn: localHandler }) =>
                this.renderLocationFilter(css.filterField, true, params =>
                  localHandler(false)(params)
                ),
            },
          ])
      : [];

    // Selected aka active filters
    const selectedFilters = validQueryParams;
    const isValidDatesFilter =
      searchParamsInURL.dates == null ||
      (searchParamsInURL.dates != null && searchParamsInURL.dates === selectedFilters.dates);
    const keysOfSelectedFilters = Object.keys(selectedFilters).filter(
      f => f !== sortConfig.queryParamName
    );
    const selectedFiltersCountForMobile = isKeywordSearch
      ? keysOfSelectedFilters.filter(f => f !== 'keywords').length
      : keysOfSelectedFilters.length;

    const hasPaginationInfo = !!pagination && pagination.totalItems != null;
    const resultsMatchPage =
      searchParamsAreInSync && (!favoritesPage || searchParams?.favorites === 1);
    const totalItems =
      resultsMatchPage && hasPaginationInfo
        ? pagination.totalItems
        : pagination?.paginationUnsupported
        ? listings.length
        : 0;
    const listingsAreLoaded =
      !searchInProgress &&
      resultsMatchPage &&
      !!(hasPaginationInfo || pagination?.paginationUnsupported);
    const noSavedFavorites =
      favoritesPage && currentUser && favoriteListingIds(currentUser).length === 0;

    const conflictingFilterActive = isAnyFilterActive(
      sortConfig.conflictingFilters,
      validQueryParams,
      filterConfigs
    );
    const sortBy = mode => {
      return sortConfig.active ? (
        <SortBy
          sort={validQueryParams[sortConfig.queryParamName]}
          isConflictingFilterActive={!!conflictingFilterActive}
          hasConflictingFilters={!!(sortConfig.conflictingFilters?.length > 0)}
          selectedFilters={selectedFilters}
          onSelect={this.handleSortBy}
          showAsPopup
          mode={mode}
          contentPlacementOffset={FILTER_DROPDOWN_OFFSET}
        />
      ) : null;
    };
    const noResultsInfo = (
      <NoSearchResultsMaybe
        listingsAreLoaded={listingsAreLoaded}
        totalItems={totalItems}
        location={location}
        resetAll={this.resetAll}
        favoritesPage={favoritesPage}
      />
    );

    const { title, description, schema } = createSearchResultSchema(
      listings,
      searchParamsInURL || {},
      intl,
      routeConfiguration,
      config
    );

    // Set topbar class based on if a modal is open in
    // a child component
    const topbarClasses = this.state.isMobileModalOpen
      ? classNames(css.topbarBehindModal, css.topbar)
      : css.topbar;

    // N.B. openMobileMap button is sticky.
    // For some reason, stickyness doesn't work on Safari, if the element is <button>
    return (
      <Page
        scrollingDisabled={scrollingDisabled}
        description={
          favoritesPage ? intl.formatMessage({ id: 'FavoritesPage.description' }) : description
        }
        title={favoritesPage ? intl.formatMessage({ id: 'FavoritesPage.title' }) : title}
        schema={favoritesPage ? null : schema}
      >
        <TopbarContainer rootClassName={topbarClasses} currentSearchParams={validQueryParams} />
        <div className={css.layoutWrapperContainer}>
          {!hideFilterSidebar && (
            <aside className={css.layoutWrapperFilterColumn} data-testid="filterColumnAside">
              <div className={css.filterColumnContent}>
                {useMapSearchFilters && this.renderLocationFilter(css.filterField)}
                {availableFilters.map(filterConfig => {
                  const key = `SearchFiltersDesktop.${filterConfig.scope || 'built-in'}.${
                    filterConfig.key
                  }`;
                  return (
                    <FilterComponent
                      key={key}
                      idPrefix="SearchFiltersDesktop"
                      className={css.filter}
                      config={filterConfig}
                      listingCategories={listingCategories}
                      marketplaceCurrency={marketplaceCurrency}
                      urlQueryParams={validQueryParams}
                      initialValues={initialValues(this.props, this.state.currentQueryParams)}
                      getHandleChangedValueFn={this.getHandleChangedValueFn}
                      intl={intl}
                      liveEdit
                      showAsPopup={false}
                      isDesktop
                    />
                  );
                })}
                {favoritesButton}
                <button className={css.resetAllButton} onClick={e => this.handleResetAll(e)}>
                  <FormattedMessage id={'SearchFiltersMobile.resetAll'} />
                </button>
              </div>
            </aside>
          )}

          <div
            className={classNames(css.layoutWrapperMain, {
              [css.layoutWrapperMainFullWidth]: hideFilterSidebar,
            })}
            role="main"
          >
            <div className={css.searchResultContainer}>
              {favoritesPage && (
                <div className={css.favoritesPageIntro}>
                  <H3 className={css.favoritesPageHeading}>
                    <FormattedMessage id="FavoritesPage.heading" />
                  </H3>
                  <p className={css.favoritesPageSubtitle}>
                    <FormattedMessage id="FavoritesPage.subtitle" />
                  </p>
                </div>
              )}
              {noSavedFavorites && (
                <div className={css.emptyFavorites}>
                  <p className={css.emptyFavoritesMessage}>
                    <FormattedMessage id="FavoritesPage.noFavorites" />
                  </p>
                  <p className={css.emptyFavoritesExplore}>
                    <NamedLink className={css.emptyFavoritesLink} name="SearchPage">
                      <FormattedMessage id="FavoritesPage.findArtists" />
                    </NamedLink>{' '}
                    <FormattedMessage id="FavoritesPage.or" />{' '}
                    <NamedLink className={css.emptyFavoritesLink} name="FlashSearchPage">
                      <FormattedMessage id="FavoritesPage.findFlash" />
                    </NamedLink>
                    .
                  </p>
                </div>
              )}
              {/* Top sticky filter bar — shown on desktop when sidebar is hidden */}
              {hideFilterSidebar && !noSavedFavorites && (
                <div className={css.topFiltersBar}>
                  {availableFilters.map(filterConfig => {
                    const key = `SearchFiltersTop.${filterConfig.scope || 'built-in'}.${
                      filterConfig.key
                    }`;
                    return (
                      <FilterComponent
                        key={key}
                        idPrefix="SearchFiltersTop"
                        config={filterConfig}
                        listingCategories={listingCategories}
                        marketplaceCurrency={marketplaceCurrency}
                        urlQueryParams={validQueryParams}
                        initialValues={initialValues(this.props, this.state.currentQueryParams)}
                        getHandleChangedValueFn={this.getHandleChangedValueFn}
                        intl={intl}
                        liveEdit
                        showAsPopup
                        isDesktop
                      />
                    );
                  })}
                  {this.renderLocationFilter(css.filterField)}
                  {favoritesButton}
                  <button className={css.resetAllButtonTop} onClick={e => this.handleResetAll(e)}>
                    <FormattedMessage id={'SearchFiltersMobile.resetAll'} />
                  </button>
                </div>
              )}

              {!noSavedFavorites && (
                <SearchFiltersMobile
                  className={css.searchFiltersMobileList}
                  urlQueryParams={validQueryParams}
                  sortByComponent={sortBy('mobile')}
                  favoritesButton={hideFilterSidebar ? favoritesButton : null}
                  listingsAreLoaded={listingsAreLoaded}
                  resultsCount={totalItems}
                  searchInProgress={searchInProgress}
                  searchListingsError={searchListingsError}
                  showAsModalMaxWidth={MODAL_BREAKPOINT}
                  onManageDisableScrolling={onManageDisableScrolling}
                  onOpenModal={this.onOpenMobileModal}
                  onCloseModal={this.onCloseMobileModal}
                  resetAll={this.resetAll}
                  selectedFiltersCount={selectedFiltersCountForMobile}
                  isMapVariant={false}
                  noResultsInfo={noResultsInfo}
                  routeName={this.props.routeName || 'SearchPage'}
                  isBottomSheetVariant={hideFilterSidebar}
                  filterItems={filterItems}
                >
                  {availableFilters.map(filterConfig => {
                    const key = `SearchFiltersMobile.${filterConfig.scope || 'built-in'}.${
                      filterConfig.key
                    }`;

                    return (
                      <FilterComponent
                        key={key}
                        idPrefix="SearchFiltersMobile"
                        config={filterConfig}
                        listingCategories={listingCategories}
                        marketplaceCurrency={marketplaceCurrency}
                        urlQueryParams={validQueryParams}
                        initialValues={initialValues(this.props, this.state.currentQueryParams)}
                        getHandleChangedValueFn={this.getHandleChangedValueFn}
                        intl={intl}
                        liveEdit
                        showAsPopup={false}
                      />
                    );
                  })}
                  {useMapSearchFilters && this.renderLocationFilter(css.filterField)}
                  {mobileFavoritesCheckbox}
                </SearchFiltersMobile>
              )}
              {!noSavedFavorites && (
                <MainPanelHeader
                  rootClassName={css.mainPanel}
                  sortByComponent={sortBy('desktop')}
                  isSortByActive={sortConfig.active}
                  listingsAreLoaded={listingsAreLoaded}
                  resultsCount={totalItems}
                  searchInProgress={searchInProgress}
                  searchListingsError={searchListingsError}
                  noResultsInfo={noResultsInfo}
                />
              )}
              {!noSavedFavorites && (
                <div
                  className={classNames(css.listingsForGridVariant, {
                    [css.newSearchInProgress]: !(listingsAreLoaded || searchListingsError),
                  })}
                >
                  {searchListingsError ? (
                    <H3 className={css.error}>
                      <FormattedMessage id="SearchPage.searchError" />
                    </H3>
                  ) : null}
                  {!isValidDatesFilter ? (
                    <H5>
                      <FormattedMessage id="SearchPage.invalidDatesFilter" />
                    </H5>
                  ) : null}
                  <SearchResultsPanel
                    className={css.searchListingsPanel}
                    listings={listings}
                    pagination={listingsAreLoaded ? pagination : null}
                    search={parse(location.search)}
                    isMapVariant={false}
                    pageName={this.props.routeName || 'SearchPage'}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
        <FooterContainer />
      </Page>
    );
  }
}

SearchPageComponent.defaultProps = {
  listings: [],
  pagination: null,
  searchListingsError: null,
  searchParams: {},
  routeName: 'SearchPage',
  hideFilterSidebar: false,
  favoritesPage: false,
};

SearchPageComponent.propTypes = {
  listings: array,
  onManageDisableScrolling: func.isRequired,
  pagination: propTypes.pagination,
  scrollingDisabled: bool.isRequired,
  searchInProgress: bool.isRequired,
  searchListingsError: propTypes.error,
  searchParams: object,
  routeName: string,
  hideFilterSidebar: bool,
  favoritesPage: bool,
  currentUser: propTypes.currentUser,
  onReloadFavorites: func,

  // from useHistory
  history: shape({
    push: func.isRequired,
    replace: func,
  }).isRequired,
  // from useLocation
  location: shape({
    search: string.isRequired,
  }).isRequired,

  // from useIntl
  intl: intlShape.isRequired,

  // from useConfiguration
  config: object.isRequired,

  // from useRouteConfiguration
  routeConfiguration: arrayOf(propTypes.route).isRequired,
};

const EnhancedSearchPage = props => {
  const config = useConfiguration();
  const routeConfiguration = useRouteConfiguration();
  const intl = useIntl();
  const history = useHistory();
  const location = useLocation();

  const searchListingsError = props.searchListingsError;
  if (isForbiddenError(searchListingsError)) {
    // This can happen if private marketplace mode is active
    return (
      <NamedRedirect
        name="SignupPage"
        state={{ from: `${location.pathname}${location.search}${location.hash}` }}
      />
    );
  }

  const { currentUser, ...restOfProps } = props;
  const isPrivateMarketplace = config.accessControl.marketplace.private === true;
  const isUnauthorizedUser = currentUser && !isUserAuthorized(currentUser);
  const hasNoViewingRightsUser = currentUser && !hasPermissionToViewData(currentUser);
  const hasUserPendingApprovalError = isErrorUserPendingApproval(searchListingsError);
  const hasNoViewingRightsError = isErrorNoViewingPermission(searchListingsError);

  if ((isPrivateMarketplace && isUnauthorizedUser) || hasUserPendingApprovalError) {
    return (
      <NamedRedirect
        name="NoAccessPage"
        params={{ missingAccessRight: NO_ACCESS_PAGE_USER_PENDING_APPROVAL }}
      />
    );
  } else if (hasNoViewingRightsUser || hasNoViewingRightsError) {
    return (
      <NamedRedirect
        name="NoAccessPage"
        params={{ missingAccessRight: NO_ACCESS_PAGE_VIEW_LISTINGS }}
      />
    );
  }

  return (
    <SearchPageComponent
      config={config}
      routeConfiguration={routeConfiguration}
      intl={intl}
      history={history}
      location={location}
      currentUser={currentUser}
      {...restOfProps}
    />
  );
};

const mapStateToProps = state => {
  const { currentUser } = state.user;
  const {
    currentPageResultIds,
    pagination,
    searchInProgress,
    searchListingsError,
    searchParams,
  } = state.SearchPage;
  const listings = getListingsById(state, currentPageResultIds);

  return {
    currentUser,
    listings,
    pagination,
    scrollingDisabled: isScrollingDisabled(state),
    searchInProgress,
    searchListingsError,
    searchParams,
  };
};

const mapDispatchToProps = dispatch => ({
  onManageDisableScrolling: (componentId, disableScrolling) =>
    dispatch(manageDisableScrolling(componentId, disableScrolling)),
  onReloadFavorites: (search, config) => dispatch(loadSearchData({}, search, config)),
});

// Note: it is important that the withRouter HOC is **outside** the
// connect HOC, otherwise React Router won't rerender any Route
// components since connect implements a shouldComponentUpdate
// lifecycle hook.
//
// See: https://github.com/ReactTraining/react-router/issues/4671
const SearchPage = compose(
  connect(
    mapStateToProps,
    mapDispatchToProps
  )
)(EnhancedSearchPage);

export default SearchPage;
